import { useState, useEffect } from 'react';
import { Zap, CheckCircle2, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';
import { apiCall, shuffleArray } from './api';
import { getT } from './i18n';

/**
 * Défi quotidien — 1 question boostée, +40 XP si correct.
 * Même question pour tous chaque jour (déterministe côté backend).
 * La direction suit le choix de l'utilisateur (localStorage academyLearnDir).
 */
export default function DailyChallenge({ token, lang = 'fr', onXpGained }) {
  const t = getT(lang);
  const [data, setData] = useState(null);
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [result, setResult] = useState(null); // {correct, xp_gained, expected}
  const direction = (typeof window !== 'undefined' && localStorage.getItem('academyLearnDir')) || 'fr2en';

  useEffect(() => {
    apiCall(`/daily-challenge?direction=${direction}`, 'GET', null, token)
      .then(d => setData(d ? { ...d, options: shuffleArray(d.options) } : d))
      .catch(() => {});
  }, [token, direction]);

  const submit = async () => {
    if (!answer.trim() || submitting) return;
    setSubmitting(true);
    try {
      const r = await apiCall('/daily-challenge/complete', 'POST', { answer, direction }, token);
      setResult(r);
      if (r.correct) {
        toast.success(`+${r.xp_gained} XP ${lang === 'fr' ? '— Défi réussi !' : '— Challenge complete!'}`);
        onXpGained && onXpGained(r.xp_gained);
        setData(d => d ? { ...d, completed: true } : d);
      } else {
        toast.error(lang === 'fr' ? 'Pas tout à fait — réessaie demain !' : 'Not quite — try again tomorrow!');
      }
    } catch (e) {
      toast.error(e.message);
    }
    setSubmitting(false);
  };

  if (!data) return null;

  return (
    <div className="bg-gradient-to-br from-yellow-500/15 via-orange-500/10 to-amber-500/15 border-2 border-yellow-400/40 rounded-2xl p-5" data-testid="daily-challenge-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center"><Zap size={16} className="text-slate-950" /></div>
          <div>
            <h3 className="font-bold">{t('dc_title')}</h3>
            <div className="text-[10px] text-yellow-200/70 uppercase tracking-wider">+{data.reward_xp} XP</div>
          </div>
        </div>
        {data.completed && (
          <div className="flex items-center gap-1 text-emerald-300 text-xs font-bold" data-testid="dc-done-badge">
            <CheckCircle2 size={14} />{t('dc_done')}
          </div>
        )}
      </div>

      <div className="bg-slate-950/40 rounded-lg p-3 mb-3 text-sm font-medium" data-testid="dc-question">
        {data.question}
      </div>

      {data.completed || result?.correct ? (
        <div className="text-center py-3 text-emerald-300 text-sm" data-testid="dc-completed-message">
          ✨ {t('dc_come_back')}
        </div>
      ) : (
        <>
          <div className="space-y-2 mb-2" data-testid="dc-options">
            {(data.options || []).map((opt, oi) => {
              const chosen = answer === opt;
              return (
                <button key={oi} onClick={() => setAnswer(opt)} disabled={submitting}
                  data-testid={`dc-opt-${oi}`}
                  className={`w-full text-left px-3 py-2 rounded-lg border transition text-sm ${chosen ? 'bg-yellow-400/20 border-yellow-400/60' : 'bg-slate-900/60 border-white/10 hover:border-white/30'}`}>
                  {opt}
                </button>
              );
            })}
          </div>
          {showHint && (
            <div className="text-[11px] text-yellow-200/80 mb-2 italic flex items-start gap-1">
              <Lightbulb size={12} className="mt-0.5 flex-shrink-0" />{data.hint}
            </div>
          )}
          {result && !result.correct && (
            <div className="text-[11px] text-red-300 mb-2" data-testid="dc-wrong">
              {t('dc_expected')} <strong>{result.expected}</strong>
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={submit} disabled={submitting || !answer} data-testid="dc-submit"
              className="flex-1 bg-yellow-500 hover:bg-yellow-400 disabled:opacity-40 text-slate-950 font-bold py-2 rounded-lg text-sm">
              {submitting ? '…' : t('dc_submit')}
            </button>
            <button onClick={() => setShowHint(true)} disabled={showHint} data-testid="dc-hint-btn"
              className="bg-white/10 hover:bg-white/20 disabled:opacity-40 px-3 rounded-lg text-xs flex items-center gap-1">
              <Lightbulb size={12} />{t('dc_hint')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
